import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";
import type {
  InsightItem as InsightItemType,
  DataValues,
  NewNarrativeData,
  BookmarkData,
  NarrativeMetadata,
  ColorPalette,
} from "../types/narrative";
import { replacePlaceholders } from "../utils/placeholders";
import { Icon } from "./Icon";
import { AnimatedText, type AnimationStyle } from "./AnimatedText";
import { TableOfContents } from "./TableOfContents";
import { extractTOCSections } from "./tocUtils";
import { QuizQuestion } from "./QuizQuestion";
import { AnimationOverlays } from "./AnimationOverlays";
import { BookmarkButton } from "./BookmarkButton";
import { CommentInput } from "./CommentInput";
import { BarChartAnimator } from "@juiceforward/datashorts";
import { HighlightsGallery } from "./HighlightsGallery";
import { MediaShowcase } from "./MediaShowcase";
import { MusicChoice } from "./MusicChoice";
import { ContentAuthorBadge } from "./ContentAuthorBadge";
import { useUserInputContext } from "../hooks/useUserInputContext";
import { useSessionContext } from "../contexts/useSessionContext";
import { MotionWrapper, ButtonMotionWrapper } from "./MotionWrapper";
import { ANIMATION_DELAYS } from "../constants/animations";
import { logInfo, logWarning, logError } from "../utils/logger";
import type { RefObject } from "react";

interface InsightItemProps {
  item: InsightItemType;
  data: DataValues;
  animationStyle: AnimationStyle;
  animationSpeed?: number;
  delay: number;
  onComplete: () => void;
  selectedSections?: Set<string>;
  onSectionToggle?: (sectionId: string, isSelected: boolean) => void;
  onContinue?: () => void;
  onReactionSelect?: () => void;
  skipAnimation?: boolean;
  narrativeData?: NewNarrativeData; // Optional - required for highlights_gallery type
  metadata?: NarrativeMetadata; // Optional - for curator resolution in contentAuthor
  groupId?: string; // Unique group ID for quiz/comment state persistence
  onPlayWithMusic?: () => void; // Called when user chooses to play with music
  onMuteMusic?: () => void; // Called when user chooses to continue without music
  // Bookmark-related props for cta_bookmark items
  contentRef?: RefObject<HTMLDivElement | null>;
  sectionId?: string;
  colorPalette?: ColorPalette; // Section's color palette for bookmark capture
  isBookmarked?: boolean;
  onBookmarkClick?: (snapshot: BookmarkData | null) => void;
  bookmarkButtonRef?: RefObject<HTMLDivElement | null>;
  format?: string | null;
}

export function InsightItem({
  item,
  data,
  animationStyle,
  animationSpeed,
  delay,
  onComplete,
  selectedSections,
  onSectionToggle,
  onContinue,
  onReactionSelect,
  skipAnimation = false,
  narrativeData,
  metadata,
  groupId,
  onPlayWithMusic,
  onMuteMusic,
  // Bookmark props
  contentRef,
  sectionId,
  colorPalette,
  isBookmarked,
  onBookmarkClick,
  bookmarkButtonRef,
  format,
}: InsightItemProps) {
  // Get tooltip state from context - must be called at top level
  const { hasSeenBookmarkTooltip } = useUserInputContext();
  const { recordAction } = useSessionContext();
  const [actionFired, setActionFired] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const isVideoMode = format === "video";

  const handleTextComplete = () => {
    onComplete();
  };

  // Item-level animation overrides exist only on text items (per the schema);
  // other item types fall back to the group-level values (speed defaults to 1).
  const effectiveAnimationStyle =
    (item.type === "text" ? item.animationStyle : undefined) ?? animationStyle;
  const effectiveAnimationSpeed =
    (item.type === "text" ? item.animationSpeed : undefined) ??
    animationSpeed ??
    1;

  // Resolve "curator" contentAuthor to the actual curator name/photo/role from metadata
  const resolveContentAuthor = (
    author: string | undefined,
  ): { name: string; photo?: string; role?: string } | undefined => {
    if (!author) return undefined;

    // Special "curator" value resolves to metadata.curator
    if (author.toLowerCase() === "curator") {
      if (metadata?.curator?.name) {
        return {
          name: metadata.curator.name,
          photo: metadata.curator.photo,
          role: metadata.curator.role,
        };
      }
      // Fallback to literal "curator" if no curator in metadata
      return { name: "curator" };
    }

    // Any other string is used directly (no photo/role available)
    return { name: author };
  };

  // Render the item content based on type
  const renderItemContent = () => {
    switch (item.type) {
      case "text": {
        const processedText = item.content
          ? replacePlaceholders(item.content, data)
          : "";

        const resolvedAuthor = resolveContentAuthor(item.contentAuthor);
        const hasAuthor = !!resolvedAuthor;

        // Comment-style rendering when contentAuthor is present
        if (hasAuthor) {
          const isAI =
            resolvedAuthor!.name.toLowerCase() === "ai" ||
            resolvedAuthor!.name.toLowerCase() === "jane ai";
          const borderColor = isAI
            ? "border-emerald-500/40"
            : "border-amber-500/40";
          const bgColor = isAI ? "bg-emerald-500/5" : "bg-amber-100/20";
          const textColor = isAI ? "text-emerald-200" : "text-amber-200";

          return (
            <MotionWrapper
              layout
              preset="fadeLeft"
              delay={delay}
              skipAnimation={skipAnimation}
            >
              <div
                className={`flex flex-col gap-2 pl-4 backdrop-blur-sm py-3 pr-3 border-l-2 ${borderColor} ${bgColor} rounded-r-2xl`}
              >
                <div
                  className={`${textColor} font-light leading-relaxed text-base whitespace-pre-line`}
                >
                  <AnimatedText
                    text={processedText}
                    style={effectiveAnimationStyle}
                    speed={effectiveAnimationSpeed}
                    skipAnimation={skipAnimation}
                    onComplete={handleTextComplete}
                  />
                </div>
                <ContentAuthorBadge
                  author={resolvedAuthor!.name}
                  photo={resolvedAuthor!.photo}
                  role={resolvedAuthor!.role}
                />
              </div>
            </MotionWrapper>
          );
        }

        // Standard text rendering (no author)
        const styleObj =
          item.style && typeof item.style === "object" ? item.style : null;
        const stylePreset =
          typeof item.style === "string"
            ? item.style
            : (styleObj?.preset ?? "body");
        const isHeadline = stylePreset === "headline";
        const isHeadlineLarge = stylePreset === "headline_large";
        const isCaption = stylePreset === "caption";

        // Determine styling based on text style - uses CSS custom properties for responsive scaling
        let textClassName = "leading-relaxed text-white"; // body default
        let textStyle: React.CSSProperties = {
          fontSize: "var(--text-scale-body)",
          lineHeight: "var(--text-scale-leading-body)",
          fontFamily: "var(--font-narrative-body)",
          fontWeight:
            "var(--font-narrative-body-weight, 300)" as unknown as number,
        };

        if (isHeadlineLarge) {
          textClassName = "text-white";
          textStyle = {
            fontSize: "var(--text-scale-headline-lg)",
            lineHeight: "var(--text-scale-leading-headline-lg, 1.22)",
            fontFamily: "var(--font-narrative-header)",
            fontWeight:
              "var(--font-narrative-header-weight, 700)" as unknown as number,
          };
        } else if (isHeadline) {
          textClassName = "leading-tight text-white";
          textStyle = {
            fontSize: "var(--text-scale-headline)",
            lineHeight: "var(--text-scale-leading-headline)",
            fontFamily: "var(--font-narrative-header)",
            fontWeight:
              "var(--font-narrative-header-weight, 600)" as unknown as number,
          };
        } else if (isCaption) {
          textClassName = "leading-relaxed text-white/70";
          textStyle = {
            fontSize: "var(--text-scale-caption)",
            fontFamily: "var(--font-narrative-body)",
            fontWeight:
              "var(--font-narrative-body-weight, 300)" as unknown as number,
          };
        }

        // Apply object overrides
        if (styleObj?.textSize) textStyle.fontSize = styleObj.textSize;
        if (styleObj?.textColor) textStyle.color = styleObj.textColor;
        if (styleObj?.textAlign) textStyle.textAlign = styleObj.textAlign;

        // Animation override: style object > item-level field > group-level
        const textEffectiveAnimationStyle =
          styleObj?.animationStyle ?? effectiveAnimationStyle;
        const textEffectiveAnimationSpeed =
          styleObj?.animationSpeed ?? effectiveAnimationSpeed;

        return (
          <MotionWrapper
            layout
            preset="fadeIn"
            delay={delay}
            skipAnimation={skipAnimation}
          >
            <div className={textClassName} style={textStyle}>
              <AnimatedText
                text={processedText}
                style={textEffectiveAnimationStyle}
                speed={textEffectiveAnimationSpeed}
                skipAnimation={skipAnimation}
                onComplete={handleTextComplete}
              />
            </div>
          </MotionWrapper>
        );
      }

      case "cta_button": {
        const buttonLabel = item.content
          ? replacePlaceholders(item.content, data)
          : "Share";
        const buttonUrl = item.url
          ? replacePlaceholders(item.url, data)
          : undefined;
        const buttonAction = item.action;
        // Feedback buttons (no URL) toggle to a thank-you state once clicked;
        // link buttons keep their label so they can be followed again. The toggle
        // is independent of tracking mode, so action === false still shows it.
        const isToggled = actionFired && !buttonUrl;

        return (
          <ButtonMotionWrapper
            delay={delay + ANIMATION_DELAYS.normal}
            skipAnimation={skipAnimation}
            onComplete={onComplete}
            className={`flex justify-center ${isVideoMode ? "opacity-0 pointer-events-none" : ""}`}
          >
            <button
              onClick={() => {
                logInfo("CTA button clicked:", buttonLabel);
                if (!actionFired) {
                  // Record by default as "buttonClick"; a custom item.action
                  // overrides the name, and action === false disables tracking
                  // only — the visual feedback state still fires below.
                  if (buttonAction !== false) {
                    recordAction(
                      typeof buttonAction === "string"
                        ? buttonAction
                        : "buttonClick",
                      {
                        groupId,
                        label: buttonLabel,
                        ...(buttonUrl ? { url: buttonUrl } : {}),
                        ...(item.icon ? { icon: item.icon } : {}),
                      },
                    );
                  }
                  setActionFired(true);
                }
                if (buttonUrl) {
                  window.open(buttonUrl, "_blank", "noopener,noreferrer");
                }
              }}
              className={`flex items-center gap-2 px-4 py-3 text-sm rounded-full font-medium shadow-lg cursor-pointer backdrop-blur-md border transition-colors ${
                isToggled
                  ? "bg-white/30 text-white border-white/40"
                  : "bg-white/10 hover:bg-white/20 text-white border-white/20"
              }`}
            >
              <Icon icon={item.icon} />
              {isToggled ? "Thanks for the feedback!" : buttonLabel}
            </button>
          </ButtonMotionWrapper>
        );
      }

      case "cta_bookmark": {
        // Check if this should show the tooltip (first bookmark button user encounters)
        const isFirstBookmarkButton = !hasSeenBookmarkTooltip;

        return (
          <MotionWrapper
            layout
            preset="fadeUp"
            delay={delay + ANIMATION_DELAYS.normal}
            skipAnimation={skipAnimation}
            onComplete={onComplete}
            className={`flex justify-center ${isVideoMode ? "opacity-0 pointer-events-none" : ""}`}
          >
            <div ref={bookmarkButtonRef as React.RefObject<HTMLDivElement>}>
              <BookmarkButton
                onClick={onBookmarkClick}
                delay={0}
                isBookmarked={isBookmarked}
                contentRef={contentRef}
                sectionId={sectionId}
                colorPalette={colorPalette}
                isFirstBookmarkButton={isFirstBookmarkButton}
              />
            </div>
          </MotionWrapper>
        );
      }

      case "cta_comment": {
        // Generate unique comment ID from groupId
        const commentGroupId = groupId || `comment-${Date.now()}`;
        // Pass the configured placeholder only when set; CommentInput supplies the
        // same default for display, so leaving it undefined keeps the placeholder
        // out of recorded engagement metadata unless the author configured one.
        const placeholderText = item.placeholder
          ? replacePlaceholders(item.placeholder, data)
          : undefined;

        return (
          <MotionWrapper
            layout
            preset="fadeUp"
            delay={delay + ANIMATION_DELAYS.normal}
            skipAnimation={skipAnimation}
            onComplete={onComplete}
            className={`w-full ${isVideoMode ? "opacity-0 pointer-events-none" : ""}`}
          >
            <CommentInput
              groupId={commentGroupId}
              placeholder={placeholderText}
              action={item.action}
            />
          </MotionWrapper>
        );
      }

      case "table_of_contents": {
        // Provide default values if props are missing
        const defaultSelectedSections = new Set<string>();
        const defaultOnSectionToggle = () => {};
        const defaultOnContinue = () => {};

        // Extract TOC sections from narrative data
        const tocSections = narrativeData
          ? extractTOCSections(narrativeData.sections)
          : [];

        return (
          <MotionWrapper
            layout
            preset="fadeUp"
            delay={delay + ANIMATION_DELAYS.normal}
            skipAnimation={skipAnimation}
            onComplete={onComplete}
            className={`w-full ${isVideoMode ? "opacity-0 pointer-events-none" : ""}`}
          >
            <TableOfContents
              sections={tocSections}
              selectedSections={selectedSections || defaultSelectedSections}
              onSelectionChange={onSectionToggle || defaultOnSectionToggle}
              onContinue={onContinue || defaultOnContinue}
              selectSections={item.selectSections}
            />
          </MotionWrapper>
        );
      }

      case "quiz": {
        if (!item.quiz) {
          logWarning("Quiz item missing quiz data");
          return null;
        }

        // Generate unique quiz ID from groupId
        const quizId = groupId ? `${groupId}-quiz` : `quiz-${Date.now()}`;

        return (
          <MotionWrapper
            preset="fadeUp"
            delay={delay + ANIMATION_DELAYS.normal}
            skipAnimation={skipAnimation}
            onComplete={onComplete}
            className={`flex justify-center ${isVideoMode ? "pointer-events-none" : ""}`}
          >
            <QuizQuestion
              quiz={item.quiz}
              data={data}
              quizId={quizId}
              action={item.action}
              onComplete={() => {}}
              onAdvance={onReactionSelect}
            />
          </MotionWrapper>
        );
      }

      case "bar_chart": {
        if (!item.barChart || !item.barChart.frames) {
          logWarning("bar_chart item missing barChart data or frames");
          return null;
        }

        // Process frames: replace placeholders and parse numeric values
        // IMPORTANT: Preserve key order from the original frame
        const processedFrames = item.barChart.frames.map((frame) => {
          // Create entries array to maintain order
          // Support both simple values and ItemConfig objects
          type ProcessedValue =
            | string
            | number
            | {
                value: number;
                dataItemStyle?: "striped" | "solid";
                color?: string;
                valueLabels?: boolean;
              };
          const entries: Array<[string, ProcessedValue]> = [];

          // Process each key in original order
          for (const key of Object.keys(frame)) {
            const value = frame[key];

            if (key === "label") {
              // Handle label separately - it can be a string or undefined
              if (value !== undefined) {
                const labelValue =
                  typeof value === "string" && value.includes("{{")
                    ? replacePlaceholders(value, data)
                    : String(value);
                entries.push(["label", labelValue]);
              }
              continue;
            }

            // Replace placeholders in the KEY (used as bar label) if it contains {{}}
            const processedKey = key.includes("{{")
              ? replacePlaceholders(key, data)
              : key;

            // Check if value is an ItemConfig object (has a 'value' property)
            const isItemConfig =
              typeof value === "object" && value !== null && "value" in value;

            if (isItemConfig) {
              // Handle ItemConfig structure: { value, dataItemStyle, color, etc. }
              const itemConfig = value as {
                value: string | number;
                dataItemStyle?: "striped" | "solid";
                color?: string;
                valueLabels?: boolean;
              };

              // Replace placeholders in the nested value
              const replacedValue =
                typeof itemConfig.value === "string" &&
                itemConfig.value.includes("{{")
                  ? replacePlaceholders(itemConfig.value, data)
                  : itemConfig.value;

              // Parse numeric value
              const numericValue =
                typeof replacedValue === "string"
                  ? parseFloat(replacedValue.replace(/[^0-9.-]/g, ""))
                  : typeof replacedValue === "number"
                    ? replacedValue
                    : 0;

              // Reconstruct ItemConfig with processed value
              entries.push([
                processedKey,
                {
                  ...itemConfig,
                  value: numericValue,
                },
              ]);
            } else {
              // Handle simple string/number values (original logic)
              const replacedValue =
                typeof value === "string" && value.includes("{{")
                  ? replacePlaceholders(value, data)
                  : value;

              // Parse numbers from formatted strings (e.g., "**35,961**" -> 35961)
              const numericValue =
                typeof replacedValue === "string"
                  ? parseFloat(replacedValue.replace(/[^0-9.-]/g, ""))
                  : typeof replacedValue === "number"
                    ? replacedValue
                    : 0;

              entries.push([processedKey, numericValue]);
            }
          }

          // Convert entries back to object (maintains insertion order)
          return Object.fromEntries(entries);
        });

        // The chart is responsive by default: omitting `width` lets it fill its
        // container. Authors can still supply `width` as a fixed-width override,
        // which opts out of that auto-sizing. `height` remains author-controlled.
        const {
          title,
          frames: _unusedFrames,
          height,
          style: userStyle,
          ...chartProps
        } = item.barChart;

        const chartHeight = height ?? 360;

        return (
          <MotionWrapper
            layout
            preset="fadeUp"
            delay={0}
            skipAnimation={skipAnimation}
            onComplete={onComplete}
            className="flex justify-center"
          >
            <div className="w-full max-w-2xl">
              {title && (
                <h3
                  className="text-white text-center mb-4 font-semibold"
                  style={{ fontSize: "var(--text-scale-headline)" }}
                >
                  {replacePlaceholders(title, data)}
                </h3>
              )}
              <BarChartAnimator
                frames={processedFrames}
                height={chartHeight}
                style={userStyle}
                {...chartProps}
                // Render the chart statically at its final state (no grow-in /
                // count-up / loop) under reduced motion, and whenever item
                // animation is skipped (backward nav, PDF export, animationStyle
                // "none"). Bars are essential data, so we show the end state — we
                // never hide the chart. Placed after the spread so author config
                // can't re-enable animation in these cases.
                animate={!skipAnimation && !prefersReducedMotion}
              />
            </div>
          </MotionWrapper>
        );
      }

      case "highlights_gallery": {
        // Highlights gallery requires narrativeData
        if (!narrativeData) {
          logError("HighlightsGallery requires narrativeData prop");
          return null;
        }

        // Use a component wrapper to call onComplete after mount (not during render)
        const HighlightsGalleryWrapper = () => {
          useEffect(() => {
            // Call onComplete after component mounts - gallery manages its own interactions
            onComplete();
          }, []);

          return (
            <HighlightsGallery
              narrativeData={narrativeData}
              data={data}
              showAllCards={item.showAllCards}
              onComplete={onContinue}
            />
          );
        };

        return (
          <MotionWrapper
            layout
            preset="fadeUp"
            delay={delay}
            skipAnimation={skipAnimation}
            className="w-full"
          >
            <HighlightsGalleryWrapper />
          </MotionWrapper>
        );
      }

      case "media_showcase": {
        if (!item.mediaShowcase) {
          logWarning("media_showcase item missing mediaShowcase data");
          return null;
        }

        return (
          <MotionWrapper
            layout
            preset="fadeUp"
            delay={delay}
            skipAnimation={skipAnimation}
            onComplete={onComplete}
            className={`w-full flex justify-center ${isVideoMode ? "opacity-0 pointer-events-none" : ""}`}
          >
            <MediaShowcase data={item.mediaShowcase} />
          </MotionWrapper>
        );
      }

      case "music_choice": {
        const handlePlayWithMusic = () => {
          onPlayWithMusic?.();
          onReactionSelect?.();
        };
        const handleContinueWithout = () => {
          onMuteMusic?.();
          onReactionSelect?.();
        };

        return (
          <ButtonMotionWrapper
            delay={delay + ANIMATION_DELAYS.normal}
            skipAnimation={skipAnimation}
            onComplete={onComplete}
            className={isVideoMode ? "opacity-0 pointer-events-none" : ""}
          >
            <MusicChoice
              playLabel={item.playLabel}
              skipLabel={item.skipLabel}
              playIcon={item.playIcon}
              skipIcon={item.skipIcon}
              onPlayWithMusic={handlePlayWithMusic}
              onContinueWithout={handleContinueWithout}
            />
          </ButtonMotionWrapper>
        );
      }

      default:
        return null;
    }
  };

  const content = renderItemContent();

  // If item has an overlay, wrap content in a relative container
  if (item.overlay && content) {
    return (
      <div className="relative">
        {/* Item-level overlay - separate layer, doesn't affect scroll */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <AnimationOverlays
            overlay={item.overlay}
            keyPrefix={`item-${groupId}`}
          />
        </div>
        {content}
      </div>
    );
  }

  return content;
}
